import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { ChipsBox, ChipToggle, AvailableChip } from './_PdiHelpers';

// Navigation-relevant subset of the 24 legacy field keys. A breadcrumb of
// "description_1 > detail_3" makes no sense, so we expose only the keys
// shops actually use as a category trail: brand + the 7 category slots.
// `category_N` chips use the per-shop label from `shopFieldLabels`
// (kasvars Code1Oms..Code7Oms) when populated; otherwise fall back to the
// cfg_* i18n string.
const ALL_LEVELS = [
    { key: 'brand',      i18nKey: 'cfg_pdi_details_brand',       fallback: 'Supplier'   },
    { key: 'category_1', i18nKey: 'cfg_pdi_details_category_1',  fallback: 'Category 1' },
    { key: 'category_2', i18nKey: 'cfg_pdi_details_category_2',  fallback: 'Category 2' },
    { key: 'category_3', i18nKey: 'cfg_pdi_details_category_3',  fallback: 'Category 3' },
    { key: 'category_4', i18nKey: 'cfg_pdi_details_category_4',  fallback: 'Category 4' },
    { key: 'category_5', i18nKey: 'cfg_pdi_details_category_5',  fallback: 'Category 5' },
    { key: 'category_6', i18nKey: 'cfg_pdi_details_category_6',  fallback: 'Category 6' },
    { key: 'category_7', i18nKey: 'cfg_pdi_details_category_7',  fallback: 'Category 7' },
];
const DEFAULTS = ['category_2', 'category_3'];

const CategoryBreadcrumbCard = ({ value = {}, onChange, shopFieldLabels, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const used   = Array.isArray(value.used) ? value.used : DEFAULTS;
    const usedSet = new Set(used);
    const available = ALL_LEVELS.filter(l => !usedSet.has(l.key)).map(l => l.key);

    const setUsed = (newUsed) => onChange && onChange('used', newUsed);

    const labelFor = (k) => {
        const shopLabel = shopFieldLabels && shopFieldLabels[k];
        if (shopLabel) return shopLabel;
        const meta = ALL_LEVELS.find(l => l.key === k);
        return meta ? tr(meta.i18nKey, meta.fallback) : k;
    };

    return (
        <ConfigCard
            title={tr('cfg_pdi_breadcrumb_title', 'Category breadcrumb')}
            description={tr('cfg_pdi_breadcrumb_desc', 'The category trail shown on the product page so visitors can easily filter back.')}
            theme={theme}
        >
            <ChipsBox>
                {used.map(k => (
                    <ChipToggle
                        key={k}
                        label={labelFor(k)}
                        onRemove={() => setUsed(used.filter(x => x !== k))}
                    />
                ))}
                {used.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', px: 1, py: 0.5 }}>
                        {tr('cfg_pdi_breadcrumb_empty', 'No levels — pick from the available row below.')}
                    </Typography>
                )}
            </ChipsBox>
            {available.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                    {available.map(k => (
                        <AvailableChip
                            key={k}
                            label={labelFor(k)}
                            onAdd={() => setUsed([...used, k])}
                        />
                    ))}
                </Box>
            )}
        </ConfigCard>
    );
};

CategoryBreadcrumbCard.propTypes = {
    value:           PropTypes.object,
    onChange:        PropTypes.func,
    shopFieldLabels: PropTypes.object,
    theme:           PropTypes.string,
    t:               PropTypes.func,
};

export default CategoryBreadcrumbCard;
